/**
 * Sticky top-bar страницы репо.
 *
 * Содержит: ◄ навигация-назад на HomePage, имя Юнита/репо, RefSelector
 * (ветка/тег/PR), счётчик health-findings. На mobile дополнительно —
 * hamburger для открытия sidebar drawer'а.
 */

import { ActionIcon, Anchor, Badge, Box, Group, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import { IconFileText, IconGitCompare, IconLayoutKanban, IconMenu2, IconPrinter, IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { Snapshot } from '@pflow/shared/v2';
import { RefSelector } from './RefSelector';
import { HelpMenu } from './HelpMenu';
import { HealthPanel } from './HealthPanel';
import { useUiStore } from '../stores/ui-store';
import { DESKTOP_QUERY } from '../lib/breakpoints';

interface Props {
    snapshot:    Snapshot;
    currentRef:  string | undefined;
    onRefChange: (ref: string | undefined) => void;
    onSync?:     () => void;
    syncStatus?: 'idle' | 'syncing';
    syncError?:  string;
    onPrint?:    () => void;
}

export function TopBar({ snapshot, currentRef, onRefChange, onSync, syncStatus, syncError, onPrint }: Props): JSX.Element {
    const isDesktop   = useMediaQuery(DESKTOP_QUERY, true);
    const openSidebar = useUiStore(s => s.openSidebar);
    const [healthOpen, setHealthOpen] = useState(false);

    const totalFindings = snapshot.health.reduce((s, h) => s + h.count, 0);
    const critical      = snapshot.health.some(h => h.severity === 'critical');
    const attention     = snapshot.health.some(h => h.severity === 'attention');
    const badgeColor    = critical ? 'red' : (attention ? 'yellow' : 'green');
    const modelErrors   = snapshot.diagnostics?.errors ?? 0;
    const title         = snapshot.unit?.name ?? `${snapshot.meta.owner}/${snapshot.meta.repo}`;

    // Ссылки на под-страницы (План/Отчёты) обязаны нести текущие ref и path:
    // без path в мультипроектном репо под-страница собрала бы снапшот по
    // ВСЕМУ репозиторию (все .dflow, включая соседние проекты).
    const subQuery = (() => {
        const q = new URLSearchParams();
        if (currentRef) q.set('ref', currentRef);
        if (snapshot.meta.subpath) q.set('path', snapshot.meta.subpath);
        const s = q.toString();
        return s ? `?${s}` : '';
    })();

    return (
        <>
            <Box
                component="header"
                data-no-print
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'var(--mantine-color-body)',
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                    padding: isDesktop ? '12px 16px' : '10px 12px',
                }}
            >
                <Group justify="space-between" align="center" wrap="nowrap" gap={isDesktop ? 'sm' : 6}>
                    <Group gap={isDesktop ? 'sm' : 6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                        {!isDesktop && (
                            <ActionIcon
                                variant="default"
                                size="md"
                                onClick={openSidebar}
                                aria-label="Открыть боковую панель"
                            >
                                <IconMenu2 size={16} />
                            </ActionIcon>
                        )}
                        {isDesktop && <Anchor component={Link} to="/" c="dimmed">◄</Anchor>}
                        <Box style={{ minWidth: 0 }}>
                            <Text fw={700} fz={isDesktop ? 'lg' : 'md'} truncate data-tour="topbar-title">{title}</Text>
                            {isDesktop && (
                                <Text fz="xs" c="dimmed" truncate>
                                    {snapshot.canvases.length} Канвас(а)
                                </Text>
                            )}
                        </Box>
                    </Group>
                    <Group gap={isDesktop ? 'xs' : 4} wrap="nowrap">
                        <span data-tour="ref-selector" style={{ display: 'inline-flex' }}>
                            <RefSelector
                                owner={snapshot.meta.owner}
                                repo={snapshot.meta.repo}
                                current={currentRef}
                                onChange={onRefChange}
                            />
                        </span>
                        {onSync && (
                            <Tooltip
                                label={syncError ?? (syncStatus === 'syncing' ? 'Проверяем обновления…' : 'Проверить обновления Git')}
                                withArrow
                            >
                                <ActionIcon
                                    variant={syncError ? 'light' : 'default'}
                                    color={syncError ? 'red' : undefined}
                                    size="md"
                                    loading={syncStatus === 'syncing'}
                                    onClick={onSync}
                                    aria-label="Проверить обновления Git"
                                >
                                    <IconRefresh size={16} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {snapshot.plan && (
                            <Tooltip label="План — дискавери-борд (гипотезы, итерации, fits)" withArrow>
                                <ActionIcon
                                    component={Link}
                                    to={`/r/${snapshot.meta.owner}/${snapshot.meta.repo}/plan${subQuery}`}
                                    variant="default"
                                    size="md"
                                    aria-label="План проверки гипотез"
                                    data-tour="nav-plan"
                                >
                                    <IconLayoutKanban size={16} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Tooltip label="Отчёты разборов (reports/)" withArrow>
                            <ActionIcon
                                component={Link}
                                to={`/r/${snapshot.meta.owner}/${snapshot.meta.repo}/reports${subQuery}`}
                                variant="default"
                                size="md"
                                aria-label="Отчёты разборов"
                                data-tour="nav-reports"
                            >
                                <IconFileText size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Сравнить версии" withArrow>
                            <ActionIcon
                                component={Link}
                                to={`/r/${snapshot.meta.owner}/${snapshot.meta.repo}/compare`}
                                variant="default"
                                size="md"
                                aria-label="Сравнить две версии"
                                data-tour="nav-compare"
                            >
                                <IconGitCompare size={16} />
                            </ActionIcon>
                        </Tooltip>
                        {onPrint && (
                            <Tooltip label="Печать / PDF (Ctrl+P)" withArrow>
                                <ActionIcon
                                    variant="default"
                                    size="md"
                                    onClick={onPrint}
                                    aria-label="Печать"
                                    data-tour="nav-print"
                                >
                                    <IconPrinter size={16} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {modelErrors > 0 && (
                            <Tooltip label="Ошибки корректности модели (валидаторы DSL) — чинятся в редакторе/агенте" withArrow>
                                <UnstyledButton onClick={() => setHealthOpen(true)} style={{ lineHeight: 1 }}>
                                    <Badge
                                        color="red"
                                        variant="filled"
                                        size={isDesktop ? 'lg' : 'md'}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        🛑 {modelErrors}
                                    </Badge>
                                </UnstyledButton>
                            </Tooltip>
                        )}
                        <Tooltip label="Показать проблемы" withArrow>
                            <UnstyledButton onClick={() => setHealthOpen(true)} style={{ lineHeight: 1 }} data-tour="health-badge">
                                <Badge
                                    color={badgeColor}
                                    variant="light"
                                    size={isDesktop ? 'lg' : 'md'}
                                    style={{ cursor: 'pointer' }}
                                >
                                    ⚠ {totalFindings}
                                </Badge>
                            </UnstyledButton>
                        </Tooltip>
                        <HelpMenu />
                    </Group>
                </Group>
            </Box>

            <HealthPanel
                health={snapshot.health}
                diagnostics={snapshot.diagnostics}
                opened={healthOpen}
                onClose={() => setHealthOpen(false)}
            />
        </>
    );
}
